import React, { useState } from 'react';
import { ChevronDown, UserPlus, Check, Store } from 'lucide-react';
import { Vendor } from '../types';

interface VendorSelectorProps {
  vendors: Vendor[];
  selectedVendorId: string | null;
  assignedVendor: { id: string; business_name: string | null } | null;
  onSelectVendor: (vendor: Vendor) => void;
  isNew: boolean;
}

export const VendorSelector: React.FC<VendorSelectorProps> = ({
  vendors,
  selectedVendorId,
  assignedVendor,
  onSelectVendor,
  isNew
}) => {
  const [isOpen, setIsOpen] = useState(false);

  const selectedVendor = vendors.find(v => v.id === selectedVendorId);

  if (!isNew) {
    // Show assigned vendor for non-new items
    if (assignedVendor) {
      return (
        <div className="flex items-center gap-2 text-green-700">
          <Store className="w-4 h-4" />
          <span className="text-sm font-medium">
            {assignedVendor.business_name}
          </span>
        </div>
      );
    }
    // return (
    //     <span className="text-sm text-gray-500 flex items-center gap-2">
    //         <Check className="w-4 h-4" />
    //         Catalog Item
    //     </span>
    // );
  }

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
          selectedVendorId
            ? 'bg-indigo-100 text-indigo-700 border border-indigo-200'
            : 'bg-amber-100 text-amber-700 border border-amber-200 hover:bg-amber-200'
        }`}
      >
        {selectedVendor ? (
          <>
            <Store className="w-4 h-4" />
            {selectedVendor.business_name}
          </>
        ) : (
          <>
            <UserPlus className="w-4 h-4" />
            Assign Vendor
          </>
        )}
        <ChevronDown
          className={`w-4 h-4 transition-transform ${isOpen ? 'rotate-180' : ''}`}
        />
      </button>

      {isOpen && (
        <>
          <div
            className="fixed inset-0 z-10"
            onClick={() => setIsOpen(false)}
          />
          <div className="light absolute z-20 mt-2 w-64 bg-white rounded-xl shadow-xl border border-gray-200 overflow-hidden text-gray-900">
            <div className="p-3 border-b border-gray-100 bg-gray-50">
              <p className="text-xs font-medium text-gray-600">
                Select a vendor for this new item
              </p>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {vendors.length === 0 ? (
                <div className="p-4 text-center text-sm text-gray-500">
                  No vendors available
                </div>
              ) : (
                vendors.map(vendor => (
                  <button
                    key={vendor.id}
                    onClick={() => {
                      onSelectVendor(vendor);
                      setIsOpen(false);
                    }}
                    className={`w-full px-4 py-3 text-left hover:bg-gray-50 flex items-center gap-3 transition-colors ${
                      selectedVendorId === vendor.id ? 'bg-indigo-50' : ''
                    }`}
                  >
                    <div
                      className={`w-8 h-8 rounded-full flex items-center justify-center ${
                        selectedVendorId === vendor.id
                          ? 'bg-indigo-600 text-white'
                          : 'bg-gray-200 text-gray-600'
                      }`}
                    >
                      <Store className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-gray-900 truncate">
                        {vendor.business_name}
                      </p>
                      {vendor.metadata?.city && (
                        <p className="text-xs text-gray-500">
                          {vendor.metadata.city}
                        </p>
                      )}
                    </div>
                    {selectedVendorId === vendor.id && (
                      <Check className="w-4 h-4 text-indigo-600" />
                    )}
                  </button>
                ))
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
